import { useFormStore } from "@features/form/form.store";
import type { TableColumn } from "@shared/types/query";
import { Button } from "@shared/ui/button";
import { Checkbox } from "@shared/ui/checkbox";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@shared/ui/dropdown-menu";
import {
	type ColumnDef,
	type ColumnFiltersState,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type RowSelectionState,
	type SortingState,
	useReactTable,
} from "@tanstack/react-table";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { TableContent } from "./table-content";
import { TablePagination } from "./table-pagination";
import TableToolbar from "./table-toolbar";

export type SearchableField = {
	value: string;
	label: string;
};

interface DataTableProps<T> {
	data: T[];
	columns: ColumnDef<T, any>[];
	isPending: boolean;
	columnMetadata: TableColumn[] | undefined;
	createLabel?: string;
	emptyTitle?: string;
	emptyDescription?: string;
}

export function CrudTable<T extends { id: string | number }>({
	data,
	columns,
	isPending,
	columnMetadata,
}: DataTableProps<T>) {
	const intl = useIntl();
	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
	const [globalFilter, setGlobalFilter] = useState("");

	const setOpenFormSheet = useFormStore((state) => state.setOpenFormSheet);

	const searchableFields: SearchableField[] = useMemo(() => {
		if (!columnMetadata?.length) return [];

		return columnMetadata
			.filter((col) => col.is_visible)
			.map((col) => ({
				value: col.column_name,
				label:
					col.column_name.charAt(0).toUpperCase() + col.column_name.slice(1).replace(/_/g, " "),
			}));
	}, [columnMetadata]);

	const [searchField, setSearchField] = useState<string>(searchableFields[0]?.value ?? "");

	const enhancedColumns = useMemo<ColumnDef<T, unknown>[]>(
		() => [
			{
				id: "__select",
				header: ({ table }) => (
					<Checkbox
						checked={
							table.getIsAllPageRowsSelected() ||
							(table.getIsSomePageRowsSelected() && "indeterminate")
						}
						onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
						aria-label={intl.formatMessage({
							id: "table.selectAll",
							defaultMessage: "Select all",
						})}
					/>
				),
				cell: ({ row }) => (
					<Checkbox
						checked={row.getIsSelected()}
						onCheckedChange={(v) => row.toggleSelected(!!v)}
						aria-label={intl.formatMessage({
							id: "table.selectRow",
							defaultMessage: "Select row",
						})}
						onClick={(e) => e.stopPropagation()}
					/>
				),
				enableSorting: false,
				size: 40,
			},
			...columns,
			{
				id: "actions",
				header: "",
				enableSorting: false,
				cell: ({ row }) => (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="h-8 w-8 transition-colors hover:bg-accent"
							>
								<MoreHorizontal className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-40">
							<DropdownMenuItem
								onClick={() => {
									console.log(row.original.id);
									setOpenFormSheet(true, row.original, "edit", row.original.id);
								}}
							>
								<Pencil className="mr-2 h-4 w-4" />
								<FormattedMessage id="table.action.edit" defaultMessage="Edit" />
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem className="text-destructive focus:text-destructive">
								<Trash2 className="mr-2 h-4 w-4" />
								<FormattedMessage id="table.action.delete" defaultMessage="Delete" />
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				),
			},
		],
		[columns, intl, setOpenFormSheet]
	);

	const table = useReactTable({
		data,
		columns: enhancedColumns,
		state: { sorting, columnFilters, rowSelection, globalFilter },
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		onRowSelectionChange: setRowSelection,
		onGlobalFilterChange: setGlobalFilter,
		globalFilterFn: (row, _columnId, value) => {
			if (!value || !searchField) return true;
			const cell = row.getValue(searchField);
			return String(cell ?? "")
				.toLowerCase()
				.includes(String(value).toLowerCase());
		},
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		initialState: { pagination: { pageSize: 10 } },
	});

	const selectedCount = Object.keys(rowSelection).length;
	const totalRows = table.getFilteredRowModel().rows.length;

	return (
		<div className="space-y-4">
			<TableToolbar
				selectedCount={selectedCount}
				searchField={searchField}
				setSearchField={setSearchField}
				globalFilter={globalFilter}
				setGlobalFilter={setGlobalFilter}
				searchableFields={searchableFields}
			/>
			<TableContent
				table={table}
				isPending={isPending}
				isEmpty={data.length === 0}
				enhancedColumns={enhancedColumns}
				onCreate={() => {}}
			/>

			<TablePagination table={table} totalRows={totalRows} />
		</div>
	);
}
